import { useEffect, useState, useRef } from "react";
import { CalendarDays, Plus, Pencil, Trash2, Eye, EyeOff, Upload, Repeat, PauseCircle, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { getAllActivities, createActivity, updateActivity, deleteActivity, uploadFile } from "@/lib/api";

const categoryOptions = [
  { value: "sport", label: "Sport", color: "bg-blue-500/20 text-blue-600" },
  { value: "workshop", label: "Workshop", color: "bg-purple-500/20 text-purple-600" },
  { value: "evenement", label: "Evenement", color: "bg-green-500/20 text-green-600" },
  { value: "training", label: "Training", color: "bg-amber-500/20 text-amber-600" }
];

const weekdayOptions = [
  { value: 0, label: "Maandag" },
  { value: 1, label: "Dinsdag" },
  { value: 2, label: "Woensdag" },
  { value: 3, label: "Donderdag" },
  { value: 4, label: "Vrijdag" },
  { value: 5, label: "Zaterdag" },
  { value: 6, label: "Zondag" }
];

const emptyForm = {
  title: "",
  description: "",
  date: "",
  time: "",
  location: "Sporthal Gent",
  category: "sport",
  image_url: "",
  max_participants: "",
  is_active: true,
  is_recurring: false,
  recurring_day: null,
  recurring_end_date: "",
  pause_periods: []
};

const emptyPausePeriod = {
  start_date: "",
  end_date: "",
  reason: ""
};

export default function AdminActivities() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newPausePeriod, setNewPausePeriod] = useState(emptyPausePeriod);
  const fileInputRef = useRef(null);

  const fetchActivities = async () => {
    try {
      const res = await getAllActivities();
      setActivities(res.data);
    } catch (error) {
      toast.error("Kon activiteiten niet laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, []);

  const handleOpenCreate = () => {
    setFormData(emptyForm);
    setNewPausePeriod(emptyPausePeriod);
    setIsEditing(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (activity) => {
    setFormData({
      title: activity.title,
      description: activity.description,
      date: activity.date,
      time: activity.time,
      location: activity.location,
      category: activity.category,
      image_url: activity.image_url || "",
      max_participants: activity.max_participants?.toString() || "",
      is_active: activity.is_active,
      is_recurring: activity.is_recurring || false,
      recurring_day: activity.recurring_day ?? null,
      recurring_end_date: activity.recurring_end_date || "",
      pause_periods: activity.pause_periods || []
    });
    setNewPausePeriod(emptyPausePeriod);
    setSelectedActivity(activity);
    setIsEditing(true);
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Alleen afbeeldingen zijn toegestaan");
      return;
    }

    setUploading(true);
    try {
      const res = await uploadFile(file);
      setFormData({...formData, image_url: res.data.url});
      toast.success("Afbeelding geüpload");
    } catch (error) {
      toast.error("Kon afbeelding niet uploaden");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description || !formData.time) {
      toast.error("Vul alle verplichte velden in");
      return;
    }

    // For recurring activities, date is optional (uses recurring_day instead)
    if (!formData.is_recurring && !formData.date) {
      toast.error("Vul een datum in voor eenmalige activiteiten");
      return;
    }

    if (formData.is_recurring && formData.recurring_day === null) {
      toast.error("Selecteer een weekdag voor terugkerende activiteiten");
      return;
    }

    const data = {
      ...formData,
      max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
      recurring_day: formData.is_recurring ? formData.recurring_day : null,
      recurring_end_date: formData.is_recurring ? formData.recurring_end_date : null,
      pause_periods: formData.is_recurring ? formData.pause_periods : []
    };

    try {
      if (isEditing) {
        await updateActivity(selectedActivity.id, data);
        toast.success("Activiteit bijgewerkt");
      } else {
        await createActivity(data);
        toast.success("Activiteit aangemaakt");
      }
      setDialogOpen(false);
      fetchActivities();
    } catch (error) {
      toast.error("Kon activiteit niet opslaan");
    }
  };

  const addPausePeriod = () => {
    if (!newPausePeriod.start_date || !newPausePeriod.end_date) {
      toast.error("Vul start- en einddatum in voor de pauze periode");
      return;
    }
    if (newPausePeriod.start_date > newPausePeriod.end_date) {
      toast.error("Startdatum moet voor einddatum liggen");
      return;
    }
    setFormData({
      ...formData,
      pause_periods: [...formData.pause_periods, { ...newPausePeriod }]
    });
    setNewPausePeriod(emptyPausePeriod);
  };

  const removePausePeriod = (index) => {
    setFormData({
      ...formData,
      pause_periods: formData.pause_periods.filter((_, i) => i !== index)
    });
  };

  const handleDelete = async () => {
    try {
      await deleteActivity(selectedActivity.id);
      toast.success("Activiteit verwijderd");
      setDeleteDialogOpen(false);
      fetchActivities();
    } catch (error) {
      toast.error("Kon activiteit niet verwijderen");
    }
  };

  const toggleActive = async (activity) => {
    try {
      await updateActivity(activity.id, { is_active: !activity.is_active });
      toast.success(activity.is_active ? "Activiteit gedeactiveerd" : "Activiteit geactiveerd");
      fetchActivities();
    } catch (error) {
      toast.error("Kon status niet wijzigen");
    }
  };

  const getCategoryInfo = (cat) => categoryOptions.find(c => c.value === cat) || categoryOptions[0];

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-slate-200 rounded" />
          <div className="h-96 bg-slate-200 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-activities-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <CalendarDays className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Activiteiten</h1>
            <p className="text-slate-600">Sport Eldorado kalender beheren</p>
          </div>
        </div>
        <Button onClick={handleOpenCreate} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="h-4 w-4 mr-2" />
          Nieuwe Activiteit
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {categoryOptions.map(cat => (
          <Card key={cat.value} className="bg-white border-slate-200">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500">{cat.label}</p>
              <p className="text-2xl font-bold text-slate-900">
                {activities.filter(a => a.category === cat.value).length}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Table */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200">
                <TableHead className="text-slate-600">Titel</TableHead>
                <TableHead className="text-slate-600">Categorie</TableHead>
                <TableHead className="text-slate-600">Datum</TableHead>
                <TableHead className="text-slate-600">Tijd</TableHead>
                <TableHead className="text-slate-600">Locatie</TableHead>
                <TableHead className="text-slate-600">Status</TableHead>
                <TableHead className="text-slate-600">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activities.length > 0 ? (
                activities.map((activity) => {
                  const catInfo = getCategoryInfo(activity.category);
                  const hasPausePeriods = activity.pause_periods && activity.pause_periods.length > 0;
                  return (
                    <TableRow key={activity.id} className="border-slate-200 hover:bg-slate-50">
                      <TableCell className="font-medium text-slate-900">
                        <div className="flex items-center gap-2 flex-wrap">
                          {activity.title}
                          {activity.is_recurring && (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                              <Repeat className="h-3 w-3 mr-1" />
                              Wekelijks
                            </Badge>
                          )}
                          {hasPausePeriods && (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                              <PauseCircle className="h-3 w-3 mr-1" />
                              {activity.pause_periods.length} pauze{activity.pause_periods.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={catInfo.color}>{catInfo.label}</Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {activity.is_recurring 
                          ? weekdayOptions.find(w => w.value === activity.recurring_day)?.label || "-"
                          : activity.date}
                      </TableCell>
                      <TableCell className="text-slate-600">{activity.time}</TableCell>
                      <TableCell className="text-slate-600">{activity.location}</TableCell>
                      <TableCell>
                        <Badge className={activity.is_active ? "bg-green-500/20 text-green-600" : "bg-slate-200 text-slate-500"}>
                          {activity.is_active ? "Actief" : "Inactief"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => toggleActive(activity)}>
                            {activity.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleOpenEdit(activity)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => { setSelectedActivity(activity); setDeleteDialogOpen(true); }}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    Geen activiteiten gevonden
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">{isEditing ? "Activiteit Bewerken" : "Nieuwe Activiteit"}</DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label className="text-slate-700">Titel *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Naam van de activiteit"
                className="border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Beschrijving *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Beschrijf de activiteit"
                rows={3}
                className="border-slate-300"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Datum {!formData.is_recurring && "*"}</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className="border-slate-300"
                  disabled={formData.is_recurring}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Tijd *</Label>
                <Input
                  value={formData.time}
                  onChange={(e) => setFormData({...formData, time: e.target.value})}
                  placeholder="bijv. 14:00 - 16:00"
                  className="border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Categorie</Label>
                <Select value={formData.category} onValueChange={(val) => setFormData({...formData, category: val})}>
                  <SelectTrigger className="border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Recurring Options */}
            <div className={`p-4 rounded-lg border ${formData.is_recurring ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center gap-3 mb-3">
                <Switch
                  checked={formData.is_recurring}
                  onCheckedChange={(checked) => setFormData({...formData, is_recurring: checked, recurring_day: checked ? 0 : null, pause_periods: []})}
                />
                <Label className="text-slate-700 font-medium flex items-center gap-2">
                  <Repeat className="h-4 w-4" />
                  Wekelijks terugkerende activiteit
                </Label>
              </div>
              
              {formData.is_recurring && (
                <>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-slate-700">Weekdag *</Label>
                      <Select 
                        value={formData.recurring_day?.toString() || "0"} 
                        onValueChange={(val) => setFormData({...formData, recurring_day: parseInt(val)})}
                      >
                        <SelectTrigger className="border-slate-300 bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {weekdayOptions.map(w => (
                            <SelectItem key={w.value} value={w.value.toString()}>{w.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700">Einddatum (optioneel)</Label>
                      <Input
                        type="date"
                        value={formData.recurring_end_date}
                        onChange={(e) => setFormData({...formData, recurring_end_date: e.target.value})}
                        className="border-slate-300 bg-white"
                        placeholder="Geen einddatum"
                      />
                      <p className="text-xs text-slate-500">Laat leeg voor onbeperkt</p>
                    </div>
                  </div>

                  {/* Pause Periods Section */}
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Label className="text-slate-700 font-medium flex items-center gap-2 mb-3">
                      <PauseCircle className="h-4 w-4 text-amber-600" />
                      Pauze Periodes (bijv. vakanties)
                    </Label>
                    
                    {/* Existing pause periods */}
                    {formData.pause_periods.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {formData.pause_periods.map((period, index) => (
                          <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border border-amber-200">
                            <span className="text-sm text-slate-600 flex-1">
                              {period.start_date} t/m {period.end_date}
                              {period.reason && <span className="text-amber-600 ml-2">({period.reason})</span>}
                            </span>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => removePausePeriod(index)}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add new pause period */}
                    <div className="grid grid-cols-4 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Start</Label>
                        <Input
                          type="date"
                          value={newPausePeriod.start_date}
                          onChange={(e) => setNewPausePeriod({...newPausePeriod, start_date: e.target.value})}
                          className="border-slate-300 bg-white text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Eind</Label>
                        <Input
                          type="date"
                          value={newPausePeriod.end_date}
                          onChange={(e) => setNewPausePeriod({...newPausePeriod, end_date: e.target.value})}
                          className="border-slate-300 bg-white text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Reden (optioneel)</Label>
                        <Input
                          value={newPausePeriod.reason}
                          onChange={(e) => setNewPausePeriod({...newPausePeriod, reason: e.target.value})}
                          placeholder="bijv. Zomervakantie"
                          className="border-slate-300 bg-white text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">&nbsp;</Label>
                        <Button
                          type="button"
                          onClick={addPausePeriod}
                          className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Tijdens pauze periodes wordt de activiteit niet getoond op de kalender.
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Locatie</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Max Deelnemers</Label>
                <Input
                  type="number"
                  value={formData.max_participants}
                  onChange={(e) => setFormData({...formData, max_participants: e.target.value})}
                  placeholder="Optioneel"
                  className="border-slate-300"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Afbeelding (optioneel)</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="URL of upload een afbeelding"
                  className="flex-1 border-slate-300"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  ref={fileInputRef}
                />
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="border-slate-300"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? "..." : "Upload"}
                </Button>
              </div>
              {formData.image_url && (
                <img src={formData.image_url} alt="Preview" className="w-full h-32 object-cover rounded-lg mt-2" />
              )}
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
              />
              <Label className="text-slate-700">Actief (zichtbaar op website)</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-300">Annuleren</Button>
            <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white">
              {isEditing ? "Opslaan" : "Aanmaken"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Activiteit Verwijderen</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">Weet je zeker dat je deze activiteit wilt verwijderen?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-slate-300">Annuleren</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Verwijderen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
